import React, { useRef, useState } from "react";

const SEL = 'input[data-testid^="frs-edit-qty-"]';

// The next quantity input in the grid: same column on the next row (dr) or the next cell along the row (dc).
const neighbour = (el, dr, dc) => {
  const td = el.closest("td");
  const tr = td?.parentElement;
  if (!td || !tr) return null;
  if (dc) {
    let c = dc > 0 ? td.nextElementSibling : td.previousElementSibling;
    while (c) {
      const hit = c.querySelector(SEL);
      if (hit) return hit;
      c = dc > 0 ? c.nextElementSibling : c.previousElementSibling;
    }
    return null;
  }
  const col = td.dataset.col;
  let r = dr > 0 ? tr.nextElementSibling : tr.previousElementSibling;
  while (r) {
    const cell = col ? r.querySelector(`td[data-col="${CSS.escape(col)}"] ${SEL}`) : null;
    if (cell) return cell;
    // a collapsed floor / area / building row hides its cells — hand it back so the caller can open it and keep going
    if (r.dataset.collapsed === "1") return { expand: r.querySelector('[data-testid^="grid-toggle-"]') };
    r = dr > 0 ? r.nextElementSibling : r.previousElementSibling;
  }
  return null;
};

/** Excel-like quantity cell: click selects the whole number; Enter ↓ / Tab → / Shift+Tab ← commit and move,
 *  arrows move while the value is untouched, Escape backs out, clicking away commits. */
export default function QtyEditCell({ value, staged, onCommit, testid }) {
  const [draft, setDraftState] = useState(null);
  const draftRef = useRef(null);
  const setDraft = (v) => { draftRef.current = v; setDraftState(v); };
  const editing = draft !== null;

  const commit = () => {
    if (draftRef.current === null) return;
    const raw = draftRef.current.trim();
    setDraft(null);
    const next = raw === "" ? Number(value) : Math.max(0, Number(raw));
    if (Number.isFinite(next) && next !== Number(value)) onCommit(next);
  };
  const go = (el, dr, dc) => {
    const to = neighbour(el, dr, dc);
    commit();
    if (to?.expand) {
      // open the collapsed group, then land in its first cell once it has rendered
      to.expand.click();
      setTimeout(() => { const next = neighbour(el, dr, dc); if (next && !next.expand) next.focus(); else el.focus(); }, 0);
    } else if (to) to.focus();
    else el.focus();                       // nothing further: stay put rather than dropping focus on the page
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={editing ? draft : value}
      data-testid={testid}
      onClick={(e) => e.stopPropagation()}
      onFocus={(e) => { setDraft(String(value ?? "")); e.target.select(); }}
      onMouseUp={(e) => e.preventDefault()}
      onChange={(e) => setDraft(e.target.value.replace(/[^0-9.]/g, ""))}
      onKeyDown={(e) => {
        const el = e.currentTarget;
        const untouched = draftRef.current === null || draftRef.current === String(value ?? "");
        const len = el.value.length;
        const caretAtEdge = (dir) => el.selectionStart === el.selectionEnd && (dir < 0 ? el.selectionStart === 0 : el.selectionEnd === len);
        // ↑/↓ always save and move (Excel); ←/→ move while the number is untouched or once the caret reaches its edge
        const move = { Enter: [e.shiftKey ? -1 : 1, 0], Tab: [0, e.shiftKey ? -1 : 1], ArrowDown: [1, 0], ArrowUp: [-1, 0] }[e.key]
          || ((untouched || (e.key === "ArrowRight" && caretAtEdge(1)) || (e.key === "ArrowLeft" && caretAtEdge(-1)))
              && { ArrowRight: [0, 1], ArrowLeft: [0, -1] }[e.key]);
        if (move) { e.preventDefault(); go(el, ...move); }
        else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setDraft(null); el.blur(); }
      }}
      onBlur={commit}
      className={`absolute inset-0 w-full h-full text-center tabular-nums text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 selection:bg-blue-200 dark:selection:bg-blue-800 ${
        staged ? "ring-2 ring-inset ring-amber-500 bg-amber-50 dark:bg-amber-950 font-semibold" : ""}`}
    />
  );
}
